import React from 'react'
import { Box, Button } from '@mui/material'
import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Download from 'yet-another-react-lightbox/plugins/download'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'

import GoogleIcon from '../../../components/icon/GoogleIcon'
import {
  handleDownloadAll,
  handleDownloadImage
} from '../../forms/surveys/helpers/SurveyComponentsHelper'

const PHOTO_BLUE = '#5E82AF'
const PHOTO_BLUE_HOVER = '#4F739E'

const PANELS_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
  gap: 2,
  width: '100%',
  alignItems: 'stretch'
}

const PANEL_SX = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  width: '100%',
  height: 'clamp(300px, 52vh, 500px)',
  padding: '15px 10px',
  border: '1px solid #E5E7EB',
  borderRadius: '10px',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
  boxSizing: 'border-box'
}

const STAGE_SX = {
  position: 'relative',
  flex: '1 1 auto',
  minHeight: 0,
  width: '100%',
  overflow: 'hidden',
  backgroundColor: '#FFFFFF'
}

const CONTAINED_IMAGE_SX = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  objectPosition: 'center',
  display: 'block',
  cursor: 'zoom-in',
  backgroundColor: 'transparent'
}

const DOWNLOAD_BUTTON_SX = {
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 2,
  minWidth: 0,
  height: 36,
  px: 2,
  borderRadius: '8px',
  backgroundColor: PHOTO_BLUE,
  color: '#FFFFFF',
  fontFamily: 'Lato, sans-serif',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.03em',
  lineHeight: 1,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(94, 130, 175, 0.24)',
  '&:hover': {
    backgroundColor: PHOTO_BLUE_HOVER,
    boxShadow: '0 4px 12px rgba(94, 130, 175, 0.28)'
  },
  '&:focus-visible': {
    outline: '3px solid rgba(94, 130, 175, 0.28)',
    outlineOffset: 2
  },
  '& .MuiButton-endIcon': {
    marginLeft: '8px',
    marginRight: 0,
    color: 'inherit',
    textTransform: 'none'
  },
  '& .material-symbols-outlined': {
    textTransform: 'none',
    fontFamily: '"Material Symbols Outlined"',
    lineHeight: 1
  },
  '@container (max-width: 240px)': {
    top: 6,
    right: 6,
    height: 28,
    px: 1,
    fontSize: 11,
    '& .MuiButton-endIcon': {
      marginLeft: '4px'
    }
  }
}

const DOWNLOAD_ALL_BUTTON_SX = {
  alignSelf: 'flex-start',
  mt: 1.25,
  px: 0,
  minWidth: 0,
  color: '#515465',
  fontFamily: 'Lato, sans-serif',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.2,
  textTransform: 'none',
  '&:hover': {
    color: '#1E5086',
    backgroundColor: 'transparent'
  }
}

const getGalleryGridSx = (count) => {
  if (count <= 4) {
    return {
      display: 'grid',
      flex: '1 1 auto',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gridAutoRows: 'minmax(0, 1fr)',
      gap: 1.5,
      width: '100%',
      minHeight: 0
    }
  }

  return {
    display: 'grid',
    flex: '1 1 auto',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gridAutoRows: '180px',
    gap: 1.5,
    width: '100%',
    minHeight: 0,
    overflow: 'auto',
    alignContent: 'start',
    pr: 0.5
  }
}

const TILE_SX = {
  position: 'relative',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  borderRadius: '8px',
  backgroundColor: '#F7F8FA',
  containerType: 'inline-size'
}

function PhotoDownloadButton({ onClick }) {
  return (
    <Button
      type="button"
      variant="contained"
      disableElevation
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      endIcon={<GoogleIcon name="download" size={18} />}
      sx={DOWNLOAD_BUTTON_SX}
    >
      <Box component="span" sx={{ textTransform: 'uppercase' }}>
        Descargar
      </Box>
    </Button>
  )
}

function ContainedPhoto({ src, alt, onOpen, onDownload }) {
  return (
    <Box sx={STAGE_SX}>
      <Box component="img" src={src} alt={alt} onClick={onOpen} sx={CONTAINED_IMAGE_SX} />
      <PhotoDownloadButton onClick={onDownload} />
    </Box>
  )
}

function ReferenceEmptyState() {
  return (
    <Box
      sx={{
        ...STAGE_SX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        className="no-image-container"
        style={{ width: '100%', height: '100%', aspectRatio: 'auto' }}
      >
        <img src="/assets/no-image.svg" alt="" style={{ maxWidth: '42%', maxHeight: '42%' }} />
        <strong className="mt-10">No se ha agregado ninguna imagen</strong>
      </div>
    </Box>
  )
}

function EvidenceEmptyState() {
  return (
    <Box
      sx={{
        ...STAGE_SX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div className="no-image-component" style={{ marginRight: 0 }}>
        <span className="material-symbols-outlined no-image-icon">broken_image</span>
        <span className="no-image-message">Imágenes no disponibles</span>
      </div>
    </Box>
  )
}

export function PlanimetryReferencePanel({ imageUrl, downloadName, onOpen }) {
  return (
    <Box id="imageUrlBoxElement" sx={PANEL_SX}>
      <strong className="mb-10">Imagen de referencia:</strong>
      {imageUrl ? (
        <ContainedPhoto
          src={imageUrl}
          alt="Imagen de referencia de planimetría"
          onOpen={onOpen}
          onDownload={() => handleDownloadImage(imageUrl, downloadName)}
        />
      ) : (
        <ReferenceEmptyState />
      )}
    </Box>
  )
}

export function PlanimetryEvidencePanel({
  images,
  isReady,
  hasSourceImages,
  filenameForDownloads,
  showImage,
  currentImageShow,
  onImageClick,
  onCloseLightbox
}) {
  const showEmpty = !hasSourceImages || (isReady && images.length === 0)
  const showSingle = isReady && images.length === 1
  const showGallery = isReady && images.length > 1

  return (
    <Box sx={PANEL_SX}>
      <strong className="mb-10">Foto de evidencia:</strong>
      {showEmpty && <EvidenceEmptyState />}
      {showSingle && (
        <ContainedPhoto
          src={images[0]}
          alt="Foto de evidencia 1"
          onOpen={() => onImageClick(0)}
          onDownload={() => handleDownloadImage(images[0], `${filenameForDownloads}-1`)}
        />
      )}
      {showGallery && (
        <Box sx={{ ...STAGE_SX, display: 'flex', flexDirection: 'column' }}>
          <Box sx={getGalleryGridSx(images.length)}>
            {images.map((link, index) => (
              <Box key={`${link}-${index}`} sx={TILE_SX}>
                <Box
                  component="img"
                  src={link}
                  alt={`Foto de evidencia ${index + 1}`}
                  onClick={() => onImageClick(index)}
                  sx={CONTAINED_IMAGE_SX}
                />
                <PhotoDownloadButton
                  onClick={() => handleDownloadImage(link, `${filenameForDownloads}-${index + 1}`)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {!showEmpty && !showSingle && !showGallery && <Box sx={STAGE_SX} />}
      {showGallery && (
        <Button
          type="button"
          variant="text"
          disableElevation
          onClick={() => handleDownloadAll(filenameForDownloads, images)}
          startIcon={<GoogleIcon name="download" size={18} color="#43688D" />}
          sx={DOWNLOAD_ALL_BUTTON_SX}
        >
          Descargar todas las imagenes
        </Button>
      )}
      <Lightbox
        plugins={[Captions, Zoom, Download]}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, .8)' },
          captionsDescriptionContainer: {
            display: 'flex',
            justifyContent: 'center'
          }
        }}
        open={showImage}
        close={onCloseLightbox}
        animation={{ swipe: 0 }}
        index={currentImageShow}
        zoom={{ scrollToZoom: true }}
        slides={images.map((link, index) => ({
          src: link,
          description: 'Haga doble clic en la imagen para aumentar/disminuir el zoom.',
          download: { url: link, filename: `${filenameForDownloads}-${index + 1}` }
        }))}
      />
    </Box>
  )
}

export function PlanimetryPhotoPanels(props) {
  return (
    <Box sx={PANELS_SX}>
      <PlanimetryReferencePanel
        imageUrl={props.referenceImageUrl}
        downloadName={props.downloadName}
        onOpen={props.onOpenReference}
      />
      <PlanimetryEvidencePanel
        images={props.images}
        isReady={props.isReady}
        hasSourceImages={props.hasSourceImages}
        filenameForDownloads={props.filenameForDownloads}
        showImage={props.showImage}
        currentImageShow={props.currentImageShow}
        onImageClick={props.onImageClick}
        onCloseLightbox={props.onCloseLightbox}
      />
    </Box>
  )
}
